/**
 * Superadmin Token Usage & Cost Dashboard
 *
 * Shows token usage and associated costs across organizations, agents, and models.
 *
 * @docs-internal
 * @module superadmin/SuperadminTokenUsage
 */
import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Card,
  CardHeader,
  CardContent,
  KPICard,
  StackedBarChart,
  ChartEmptyState,
  Select,
  DateRangePicker,
  Skeleton,
  SkeletonChart,
  SkeletonKPIRow,
  DataTable,
  UnderlineTabs,
  UnderlineTab,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  CurrencyDollar,
  ArrowClockwise,
  TextT,
  CubeTransparent,
  Hash,
  Warning,
} from '@phosphor-icons/react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import type { Key } from 'react-aria-components';
import {
  useTokenUsageSummary,
  useTokenUsageTimeSeries,
  useTokenUsageBreakdown,
  useTokenUsageByOrgJobType,
  useTokenUsageFilterOptions,
  useTokenUsageDailyReport,
  formatCost,
  formatTokenCount,
  formatExactNumber,
  formatExactCost,
  getDateRange,
  getGranularityForRange,
  DAYS_PRESETS,
  type TokenUsagePlatform,
  type UsageGranularity,
  type TokenUsageBreakdownRow,
  type TokenUsageDailyReport,
  type TokenUsageJobTypeOrgRow,
  type JobType,
} from './useTokenUsageData';
import type { DateRangeValue } from '../../../utils/dateRange';
import { toInclusiveIsoRange, parseIsoToDateRange } from '../../../utils/dateRange';

// ── Filter Bar ───────────────────────────────────────────────────────────────

type OrgViewMode = 'job-type' | 'totals';

type SearchParams = {
  orgId?: string;
  label?: string;
  modelId?: string;
  platform?: TokenUsagePlatform;
  days: number;
  startDate?: string;
  endDate?: string;
  reportDate?: string;
  groupBy: string;
  orgViewMode: OrgViewMode;
};

const CUSTOM_RANGE_KEY = 'custom';
const TIME_RANGE_ITEMS = [
  ...DAYS_PRESETS.map((p) => ({ id: String(p.value), label: p.label })),
  { id: CUSTOM_RANGE_KEY, label: 'Custom range' },
];

function FilterBar({
  searchParams,
  onFilterChange,
}: {
  searchParams: SearchParams;
  onFilterChange: (updates: Partial<SearchParams>) => void;
}) {
  const { data: filterOptions, isLoading } = useTokenUsageFilterOptions();
  const options = filterOptions?.data;
  const isCustomRange = Boolean(searchParams.startDate && searchParams.endDate);

  const handleSelection = (field: keyof SearchParams) => (key: Key | null) => {
    const value = key ? String(key) : undefined;
    onFilterChange({ [field]: value || undefined });
  };

  const handleTimeRangeChange = (key: Key | null) => {
    if (key === CUSTOM_RANGE_KEY) {
      // Switch to custom mode — seed with current preset range
      const { startDate, endDate } = getDateRange(searchParams.days);
      onFilterChange({ startDate, endDate });
    } else {
      // Preset selected — clear custom dates
      onFilterChange({ days: Number(key) || 30, startDate: undefined, endDate: undefined });
    }
  };

  const handleDateRangeChange = (range: DateRangeValue) => {
    const { start, end } = toInclusiveIsoRange(range);
    if (start && end) {
      onFilterChange({ startDate: start, endDate: end });
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <Flex gap="md" wrap="wrap" align="end">
          <Box className="min-w-[140px]">
            <Select
              label="Time Range"
              aria-label="Time range"
              items={TIME_RANGE_ITEMS}
              selectedKey={isCustomRange ? CUSTOM_RANGE_KEY : String(searchParams.days)}
              onSelectionChange={handleTimeRangeChange}
            />
          </Box>
          {isCustomRange && (
            <Box className="min-w-[260px]">
              <DateRangePicker
                label="Date Range"
                value={parseIsoToDateRange(searchParams.startDate, searchParams.endDate)}
                onChange={handleDateRangeChange}
                granularity="day"
                allowsNonContiguousRanges
              />
            </Box>
          )}
          <Box className="min-w-[180px]">
            {isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                label="Organization"
                aria-label="Organization"
                items={[
                  { id: '', label: 'All organizations' },
                  ...(options?.organizations.map((o) => ({
                    id: o.id,
                    label: o.name ?? o.id.slice(0, 8),
                  })) ?? []),
                ]}
                selectedKey={searchParams.orgId ?? ''}
                onSelectionChange={handleSelection('orgId')}
              />
            )}
          </Box>
          <Box className="min-w-[160px]">
            {isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                label="Agent"
                aria-label="Agent"
                items={[
                  { id: '', label: 'All agents' },
                  ...(options?.labels.map((l) => ({ id: l, label: l })) ?? []),
                ]}
                selectedKey={searchParams.label ?? ''}
                onSelectionChange={handleSelection('label')}
              />
            )}
          </Box>
          <Box className="min-w-[160px]">
            {isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                label="Model"
                aria-label="Model"
                items={[
                  { id: '', label: 'All models' },
                  ...(options?.modelIds.map((m) => ({ id: m, label: m })) ?? []),
                ]}
                selectedKey={searchParams.modelId ?? ''}
                onSelectionChange={handleSelection('modelId')}
              />
            )}
          </Box>
          <Box className="min-w-[140px]">
            <Select
              label="Platform"
              aria-label="Platform"
              items={[
                { id: '', label: 'All platforms' },
                { id: 'openai', label: 'OpenAI' },
                { id: 'anthropic', label: 'Anthropic' },
                { id: 'bedrock', label: 'Bedrock' },
                { id: 'azure', label: 'Azure' },
                { id: 'google', label: 'Google' },
              ]}
              selectedKey={searchParams.platform ?? ''}
              onSelectionChange={handleSelection('platform')}
            />
          </Box>
          <Box className="min-w-[140px]">
            <Select
              label="Group By"
              aria-label="Group by"
              items={[
                { id: 'org', label: 'Organization' },
                { id: 'label', label: 'Agent' },
                { id: 'model', label: 'Model' },
              ]}
              selectedKey={searchParams.groupBy}
              onSelectionChange={(key) => onFilterChange({ groupBy: String(key) })}
            />
          </Box>
        </Flex>
      </CardContent>
    </Card>
  );
}

/** Format Y-axis token values to fit ~6 chars: 1.2B, 45.3M, 120K, 500 */
function formatYAxisTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

function formatChartDate(dateStr: string, granularity: UsageGranularity): string {
  const d = new Date(dateStr);
  if (granularity === 'hour' || granularity === '6hour') {
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const TOKEN_CHART_SERIES = ['Input', 'Cache Read', 'Cache Write', 'Output'] as const;

/** Tooltip with exact, comma-formatted token counts (the default tooltip shows raw unformatted numbers). */
function renderTokenChartTooltip(item: Record<string, unknown>) {
  return (
    <>
      <Text size="sm" weight="medium" className="mb-1">
        {String(item.date)}
      </Text>
      {TOKEN_CHART_SERIES.map((key) => (
        <Text key={key} size="sm">
          {key}: {formatExactNumber(Number(item[key]) || 0)}
        </Text>
      ))}
    </>
  );
}

/** Tooltip with exact, comma-formatted dollar costs, one row per model series. */
function renderCostChartTooltip(modelLabels: Array<{ dataKey: string; name: string }>) {
  return (item: Record<string, unknown>) => (
    <>
      <Text size="sm" weight="medium" className="mb-1">
        {String(item.date)}
      </Text>
      {modelLabels.map(({ dataKey, name }) => (
        <Text key={dataKey} size="sm">
          {name}: {formatExactCost(Number(item[dataKey]) || 0)}
        </Text>
      ))}
    </>
  );
}

function TruncationWarning() {
  return (
    <Flex align="center" gap="xs" className="text-warning text-xs mt-2">
      <Warning size={14} weight="fill" />
      <Text size="xs" className="text-warning">
        Results truncated — narrow your filters for complete data.
      </Text>
    </Flex>
  );
}

// ── Breakdown Table ──────────────────────────────────────────────────────────

const breakdownColumns: ColumnDef<StockFeatures, TokenUsageBreakdownRow>[] = [
  {
    accessorKey: 'groupLabel',
    header: 'Name',
    cell: ({ row }) => (
      <Text size="sm" className="font-medium">
        {row.original.groupLabel ?? row.original.groupKey.slice(0, 12)}
      </Text>
    ),
  },
  {
    accessorKey: 'costCents',
    header: 'Cost',
    cell: ({ row }) => (
      <Text size="sm" className="font-mono">
        {formatCost(row.original.costCents)}
      </Text>
    ),
  },
  {
    accessorKey: 'inputTokens',
    header: 'Input',
    cell: ({ row }) => (
      <Text size="sm" className="font-mono">
        {formatTokenCount(row.original.inputTokens)}
      </Text>
    ),
  },
  {
    accessorKey: 'cacheReadTokens',
    header: 'Cache Read',
    cell: ({ row }) => (
      <Text size="sm" className="font-mono text-muted-foreground">
        {row.original.cacheReadTokens > 0 ? formatTokenCount(row.original.cacheReadTokens) : '—'}
      </Text>
    ),
  },
  {
    accessorKey: 'cacheCreationTokens',
    header: 'Cache Write',
    cell: ({ row }) => (
      <Text size="sm" className="font-mono text-muted-foreground">
        {row.original.cacheCreationTokens > 0
          ? formatTokenCount(row.original.cacheCreationTokens)
          : '—'}
      </Text>
    ),
  },
  {
    accessorKey: 'outputTokens',
    header: 'Output',
    cell: ({ row }) => (
      <Text size="sm" className="font-mono">
        {formatTokenCount(row.original.outputTokens)}
      </Text>
    ),
  },
  {
    accessorKey: 'runs',
    header: 'Runs',
    cell: ({ row }) => (
      <Text size="sm" className="font-mono">
        {row.original.runs.toLocaleString()}
      </Text>
    ),
  },
];

// ── Job Type Breakout columns ────────────────────────────────────────────────

const JOB_TYPE_COLUMNS: ReadonlyArray<{ id: JobType; label: string }> = [
  { id: 'workflow', label: 'Workflows' },
  { id: 'pagetest', label: 'Page Tests' },
  { id: 'crawl', label: 'Crawls' },
  { id: 'chaos', label: 'Chaos' },
  { id: 'adhoc', label: 'Exploratory' },
  { id: 'other', label: 'Other / system' },
];

const jobTypeCostCell =
  (jobType: JobType) =>
  ({ row }: { row: { original: TokenUsageJobTypeOrgRow } }) => {
    const cell = row.original.byJobType[jobType];
    return (
      <Box className="text-right">
        <Text size="sm" className="font-mono">
          {formatCost(cell.costCents)}
        </Text>
        <Text size="xs" className="font-mono text-muted-foreground">
          {cell.runs.toLocaleString()} {cell.runs === 1 ? 'run' : 'runs'}
        </Text>
      </Box>
    );
  };

const jobTypeBreakoutColumns: ColumnDef<StockFeatures, TokenUsageJobTypeOrgRow>[] = [
  {
    accessorKey: 'orgName',
    header: 'Organization',
    cell: ({ row }) => (
      <Text size="sm" className="font-medium">
        {row.original.orgName ?? row.original.orgId.slice(0, 12)}
      </Text>
    ),
  },
  {
    id: 'totalCost',
    header: () => <Box className="text-right">Total cost</Box>,
    accessorFn: (row) => row.totals.costCents,
    cell: ({ row }) => (
      <Box className="text-right">
        <Text size="sm" className="font-mono font-semibold">
          {formatCost(row.original.totals.costCents)}
        </Text>
      </Box>
    ),
  },
  {
    id: 'agenticRuns',
    header: () => <Box className="text-right">Agentic test runs</Box>,
    accessorFn: (row) => row.agenticRuns,
    cell: ({ row }) => (
      <Box className="text-right">
        <Text size="sm" className="font-mono">
          {row.original.agenticRuns.toLocaleString()}
        </Text>
      </Box>
    ),
  },
  ...JOB_TYPE_COLUMNS.map<ColumnDef<StockFeatures, TokenUsageJobTypeOrgRow>>(({ id, label }) => ({
    id,
    header: () => <Box className="text-right">{label}</Box>,
    accessorFn: (row) => row.byJobType[id].costCents,
    cell: jobTypeCostCell(id),
  })),
];

function DailyReportSection({
  report,
  isLoading,
  onRefresh,
}: {
  report?: TokenUsageDailyReport;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  const topAnomalies = report?.anomalies.slice(0, 8) ?? [];

  return (
    <Card>
      <CardHeader>
        <Flex align="center" justify="between">
          <Heading level={3}>Daily Report</Heading>
          <button
            onClick={onRefresh}
            className="p-1.5 rounded-md hover:bg-muted transition-colors"
            title="Refresh daily report"
          >
            <ArrowClockwise size={16} />
          </button>
        </Flex>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Box className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-24 w-full" />
          </Box>
        ) : !report ? (
          <ChartEmptyState message="No daily report available" />
        ) : (
          <Box className="space-y-4">
            <Flex gap="md" wrap="wrap">
              <Box className="flex-1 min-w-[140px]">
                <KPICard
                  title="Report Date"
                  formattedValue={report.date}
                  icon={<Hash size={16} />}
                />
              </Box>
              <Box className="flex-1 min-w-[140px]">
                <KPICard
                  title="Daily Cost"
                  formattedValue={formatCost(report.totals.costCents)}
                  icon={<CurrencyDollar size={16} />}
                />
              </Box>
              <Box className="flex-1 min-w-[140px]">
                <KPICard
                  title="Daily Tokens"
                  formattedValue={formatTokenCount(
                    report.totals.inputTokens + report.totals.outputTokens
                  )}
                  icon={<TextT size={16} />}
                />
              </Box>
              <Box className="flex-1 min-w-[140px]">
                <KPICard
                  title="Anomalies"
                  value={report.anomalies.length}
                  icon={<Warning size={16} />}
                />
              </Box>
            </Flex>

            {report.peerFetchStatus.status === 'failed' && (
              <Text size="sm" className="text-warning">
                Dev report unavailable: {report.peerFetchStatus.error}
              </Text>
            )}
            {report.peerFetchStatus.status === 'skipped' && (
              <Text size="sm" className="text-muted-foreground">
                Dev report skipped: {report.peerFetchStatus.reason}
              </Text>
            )}

            <Box className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-3 font-medium">Environment</th>
                    <th className="text-right py-2 px-3 font-medium">Cost</th>
                    <th className="text-right py-2 px-3 font-medium">Tokens</th>
                    <th className="text-right py-2 pl-3 font-medium">Runs</th>
                  </tr>
                </thead>
                <tbody>
                  {report.environments.map((envReport) => (
                    <tr key={envReport.environment} className="border-b last:border-0">
                      <td className="py-2 pr-3">{envReport.environment}</td>
                      <td className="py-2 px-3 text-right font-mono">
                        {formatCost(envReport.totals.costCents)}
                      </td>
                      <td className="py-2 px-3 text-right font-mono">
                        {formatTokenCount(
                          envReport.totals.inputTokens + envReport.totals.outputTokens
                        )}
                      </td>
                      <td className="py-2 pl-3 text-right font-mono">
                        {envReport.totals.runs.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            <Box>
              <Heading level={4} className="mb-2">
                Top anomalies
              </Heading>
              {topAnomalies.length === 0 ? (
                <Text size="sm" className="text-muted-foreground">
                  No anomalies detected.
                </Text>
              ) : (
                <Box className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium">Org</th>
                        <th className="text-left py-2 px-3 font-medium">Env</th>
                        <th className="text-right py-2 px-3 font-medium">Cost</th>
                        <th className="text-right py-2 px-3 font-medium">Delta</th>
                        <th className="text-left py-2 pl-3 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topAnomalies.map((row) => (
                        <tr
                          key={`${row.environment}-${row.orgId}`}
                          className="border-b last:border-0"
                        >
                          <td className="py-2 pr-3">{row.orgName ?? row.orgId.slice(0, 8)}</td>
                          <td className="py-2 px-3">{row.environment}</td>
                          <td className="py-2 px-3 text-right font-mono">
                            {formatCost(row.costCents)}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">
                            {formatCost(row.costDeltaCents)}
                          </td>
                          <td className="py-2 pl-3">{row.anomalyReasons.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export function SuperadminTokenUsage() {
  const search = useSearch({ from: '/superadmin/token-usage' });
  const navigate = useNavigate();

  const searchParams: SearchParams = {
    days: ((search as Record<string, unknown>).days as number) ?? 30,
    startDate: (search as Record<string, unknown>).startDate as string | undefined,
    endDate: (search as Record<string, unknown>).endDate as string | undefined,
    reportDate: (search as Record<string, unknown>).reportDate as string | undefined,
    orgId: (search as Record<string, unknown>).orgId as string | undefined,
    label: (search as Record<string, unknown>).label as string | undefined,
    modelId: (search as Record<string, unknown>).modelId as string | undefined,
    platform: (search as Record<string, unknown>).platform as TokenUsagePlatform | undefined,
    groupBy: ((search as Record<string, unknown>).groupBy as string) ?? 'org',
    orgViewMode:
      ((search as Record<string, unknown>).orgViewMode as OrgViewMode | undefined) ?? 'job-type',
  };

  const dateRange = React.useMemo(() => {
    if (searchParams.startDate && searchParams.endDate) {
      return { startDate: searchParams.startDate, endDate: searchParams.endDate };
    }
    return getDateRange(searchParams.days);
  }, [searchParams.startDate, searchParams.endDate, searchParams.days]);

  const granularity: UsageGranularity = React.useMemo(
    () => getGranularityForRange(dateRange.startDate, dateRange.endDate),
    [dateRange.startDate, dateRange.endDate]
  );

  const filters = React.useMemo(
    () => ({
      ...dateRange,
      orgId: searchParams.orgId,
      label: searchParams.label,
      modelId: searchParams.modelId,
      platform: searchParams.platform,
    }),
    [dateRange, searchParams.orgId, searchParams.label, searchParams.modelId, searchParams.platform]
  );

  const summaryQuery = useTokenUsageSummary(filters);
  const timeSeriesQuery = useTokenUsageTimeSeries(filters, granularity);
  const breakdownQuery = useTokenUsageBreakdown(
    filters,
    searchParams.groupBy as 'org' | 'label' | 'model'
  );
  // Only fetch the job-type breakout when the org tab is active in that mode —
  // the endpoint runs two aggregation queries, no point paying for it otherwise.
  const isOrgJobTypeView =
    searchParams.groupBy === 'org' && searchParams.orgViewMode === 'job-type';
  const jobTypeBreakdownQuery = useTokenUsageByOrgJobType(filters, { enabled: isOrgJobTypeView });
  const dailyReportQuery = useTokenUsageDailyReport(searchParams.reportDate);

  const handleFilterChange = (updates: Partial<SearchParams>) => {
    navigate({
      to: '/superadmin/token-usage',
      search: { ...searchParams, ...updates },
      replace: true,
    });
  };

  const summary = summaryQuery.data?.data;
  const timeSeries = timeSeriesQuery.data?.data ?? [];
  const costByModel = timeSeriesQuery.data?.costByModel;

  const chartData = React.useMemo(
    () =>
      timeSeries.map((point) => ({
        date: formatChartDate(point.date, granularity),
        Input: Math.max(0, point.inputTokens - point.cacheReadTokens - point.cacheCreationTokens),
        'Cache Read': point.cacheReadTokens,
        'Cache Write': point.cacheCreationTokens,
        Output: point.outputTokens,
      })),
    [timeSeries, granularity]
  );

  const costModelLabels = React.useMemo(
    () =>
      (costByModel?.modelIds ?? []).map((modelId) => ({
        dataKey: modelId,
        name: modelId === 'other' ? 'Other' : modelId,
      })),
    [costByModel]
  );

  const costChartData = React.useMemo(
    () =>
      (costByModel?.points ?? []).map((point) => ({
        ...point,
        date: formatChartDate(point.date, granularity),
      })),
    [costByModel, granularity]
  );

  const costChartTooltip = React.useMemo(
    () => renderCostChartTooltip(costModelLabels),
    [costModelLabels]
  );

  return (
    <Box className="space-y-6">
      {/* Filters */}
      <FilterBar searchParams={searchParams} onFilterChange={handleFilterChange} />

      <DailyReportSection
        report={dailyReportQuery.data}
        isLoading={dailyReportQuery.isLoading}
        onRefresh={() => dailyReportQuery.refetch()}
      />

      {/* KPI Cards */}
      {summaryQuery.isLoading ? (
        <SkeletonKPIRow count={4} />
      ) : (
        <Flex gap="md" wrap="wrap">
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Total Cost"
              formattedValue={summary ? formatCost(summary.costCents) : '$0.00'}
              icon={<CurrencyDollar size={16} />}
              variant="default"
            />
          </Box>
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Input Tokens"
              formattedValue={summary ? formatTokenCount(summary.inputTokens) : '0'}
              subtitle={
                summary && summary.cacheReadTokens > 0
                  ? `${formatTokenCount(summary.cacheReadTokens)} cached`
                  : undefined
              }
              icon={<TextT size={16} />}
            />
          </Box>
          <Box className="flex-1 min-w-[160px]">
            <KPICard
              title="Output Tokens"
              formattedValue={summary ? formatTokenCount(summary.outputTokens) : '0'}
              icon={<CubeTransparent size={16} />}
            />
          </Box>
          <Box className="flex-1 min-w-[160px]">
            <KPICard title="Total Runs" value={summary?.runs ?? 0} icon={<Hash size={16} />} />
          </Box>
        </Flex>
      )}

      {/* Time Series Chart */}
      <Card>
        <CardHeader>
          <Flex align="center" justify="between">
            <Heading level={3}>Token Usage Over Time</Heading>
            <button
              onClick={() => {
                summaryQuery.refetch();
                timeSeriesQuery.refetch();
                breakdownQuery.refetch();
              }}
              className="p-1.5 rounded-md hover:bg-muted transition-colors"
              title="Refresh"
            >
              <ArrowClockwise size={16} />
            </button>
          </Flex>
        </CardHeader>
        <CardContent>
          {timeSeriesQuery.isLoading ? (
            <SkeletonChart height={240} />
          ) : chartData.length === 0 ? (
            <ChartEmptyState message="No token usage data for this period" />
          ) : (
            <>
              <StackedBarChart
                data={chartData}
                xDataKey="date"
                stacks={[
                  { dataKey: 'Input', color: 'hsl(var(--primary))' },
                  { dataKey: 'Cache Read', color: 'hsl(var(--success))' },
                  { dataKey: 'Cache Write', color: 'hsl(var(--warning))' },
                  { dataKey: 'Output', color: 'hsl(280, 60%, 55%)' },
                ]}
                height={240}
                showXAxis
                showLegend
                yAxisWidth={50}
                yAxisTickFormatter={formatYAxisTokens}
                allowDecimals={false}
                renderTooltip={renderTokenChartTooltip}
              />
              {timeSeriesQuery.data?.truncated && <TruncationWarning />}

              <Box className="mt-6 pt-6 border-t border-border">
                <Heading level={4} className="mb-3">
                  Cost by Model
                </Heading>
                {costChartData.length === 0 ? (
                  <ChartEmptyState message="No cost data for this period" />
                ) : (
                  <StackedBarChart
                    data={costChartData}
                    xDataKey="date"
                    stacks={costModelLabels}
                    height={240}
                    showXAxis
                    showLegend
                    yAxisWidth={50}
                    yAxisTickFormatter={formatCost}
                    allowDecimals={false}
                    renderTooltip={costChartTooltip}
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Breakdown Table */}
      <Card>
        <CardHeader>
          <Flex align="center" justify="between" gap="md" wrap="wrap">
            <Heading level={3}>
              Usage by{' '}
              {searchParams.groupBy === 'org'
                ? 'Organization'
                : searchParams.groupBy === 'label'
                  ? 'Agent'
                  : 'Model'}
            </Heading>
            {searchParams.groupBy === 'org' && (
              <UnderlineTabs
                selectedId={searchParams.orgViewMode}
                onSelectionChange={(id) => handleFilterChange({ orgViewMode: id as OrgViewMode })}
                layoutId="org-breakdown-mode"
              >
                <UnderlineTab id="job-type">Breakout by job type</UnderlineTab>
                <UnderlineTab id="totals">Total tokens &amp; sessions</UnderlineTab>
              </UnderlineTabs>
            )}
          </Flex>
        </CardHeader>
        <CardContent>
          {isOrgJobTypeView ? (
            jobTypeBreakdownQuery.isLoading ? (
              <Box className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </Box>
            ) : (
              <>
                <DataTable
                  columns={jobTypeBreakoutColumns}
                  data={jobTypeBreakdownQuery.data?.data ?? []}
                />
                <Text size="xs" className="text-muted-foreground mt-2">
                  Cost is attributed by the top-level run id recorded on each LLM call (workflow
                  run, page-test report, exploratory test, chaos run); crawls are matched by
                  crawler-agent label. Run counts are distinct billable runs per category — for
                  crawls that's canonical pages analyzed (crawl-level navigation/taxonomy spend is
                  included in cost but has no per-run id). "Other / system" rolls up agents not
                  pinned to a top-level run (builder chat, naming, healing, KG, etc.) — runs shows 0
                  there. Agentic test runs are counted from usage events for runs that executed at
                  least one live agent node. Attribution depends on run-id metadata, which is sparse
                  on older data.
                </Text>
                {jobTypeBreakdownQuery.data?.truncated && <TruncationWarning />}
              </>
            )
          ) : breakdownQuery.isLoading ? (
            <Box className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </Box>
          ) : (
            <>
              <DataTable columns={breakdownColumns} data={breakdownQuery.data?.data ?? []} />
              {breakdownQuery.data?.truncated && <TruncationWarning />}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
